import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/**
 * Displacement candle primitive.
 *
 * Draws JJ Simon's "displacement" bars natively on the chart:
 *   - the displacement candle's body outlined so you can see WHICH bar displaced
 *   - a triple-chevron thrust glyph with a fading motion trail (the marker icon)
 *   - grade + range readout
 *   - the fair value gap the displacement left behind, as a translucent band
 *
 * The glyph is deliberately unlike every other marker in the app (WCC rings at
 * 12px, magnet emoji at 28px) — different shape, different lane (34px).
 */

export type DisplacementStatus = 'live' | 'confirmed' | 'failed' | 'expired';

export interface DisplacementLevel {
  id: string;
  timestamp: number; // ms - the displacement candle
  direction: 'bullish' | 'bearish';
  open: number;
  high: number;
  low: number;
  close: number;
  rangePoints: number;
  grade: string; // "A+" | "A" | "B"
  score: number; // 0-100
  status: DisplacementStatus;
  createdFvg: boolean;
  fvgTop: number | null;
  fvgBottom: number | null;
  skipped: boolean;
  skipReason: string | null;
}

interface DisplacementRenderData {
  x: number;
  yGlyph: number; // where the chevron stack is centred
  yBodyTop: number;
  yBodyBottom: number;
  halfWidth: number;
  isBullish: boolean;
  grade: string;
  rangePoints: number;
  status: DisplacementStatus;
  skipped: boolean;
  fvg: { top: number; bottom: number; right: number } | null; // already in coords
  /** False when the shared layout folded the chevron block into a "+N" chip (outline + FVG still draw). */
  glyph?: boolean;
}

const BULL = '#22c55e';
const BEAR = '#ef4444';
const DIM = '#94a3b8';

/** Vertical lane for the glyph, clear of WCC (12px) and magnet (28px) markers. */
export const GLYPH_OFFSET = 34;
/** How many bars to the right the FVG band extends. */
const FVG_EXTEND_BARS = 8;

/**
 * Chevron geometry. The trail runs BACK toward the candle from the tip, so the
 * glyph reaches INWARD from GLYPH_OFFSET, not outward.
 */
const CHEVRON_COUNT = 3;
const CHEVRON_DEPTH = 4;
const CHEVRON_STEP = 4.5;

/** Grade sits LABEL_STEP past the tip; the range readout another LABEL_STEP past that. */
const LABEL_STEP = 9;
const GRADE_FONT = 9;
const RANGE_FONT = 8;

/**
 * How far the whole displacement block reaches from the candle extreme it
 * anchors on, in px, along the outward axis (up for bullish, down for bearish).
 *
 * Exported because it is the contract with every other primitive that lanes off
 * the SAME extreme — CandidatePrimitive derives its own offset from `outer` so
 * the two stack instead of colliding. Computed from the constants above rather
 * than written down, so retuning GLYPH_OFFSET or a font size drags the
 * neighbouring lane along with it. Writing the number down by hand is precisely
 * how the diamonds came to draw on top of the chevrons.
 *
 *   inner = GLYPH_OFFSET - (CHEVRON_STEP * (CHEVRON_COUNT - 1) + CHEVRON_DEPTH)
 *         = 34 - (4.5 * 2 + 4)                                              = 21
 *   outer = GLYPH_OFFSET + LABEL_STEP * 2 + RANGE_FONT / 2
 *         = 34 + 18 + 4                                                     = 56
 *
 * `outer` counts the range readout's half-height because it is drawn with
 * textBaseline 'middle', so it straddles its own baseline.
 */
export const DISPLACEMENT_BAND = {
  inner: GLYPH_OFFSET - (CHEVRON_STEP * (CHEVRON_COUNT - 1) + CHEVRON_DEPTH),
  outer: GLYPH_OFFSET + LABEL_STEP * 2 + RANGE_FONT / 2,
} as const;

/** Alpha per lifecycle status — resolved markers fade back, live ones lead. */
const STATUS_ALPHA: Record<DisplacementStatus, number> = {
  live: 1.0,
  confirmed: 0.9,
  failed: 0.4,
  expired: 0.3,
};

class DisplacementPaneRenderer implements IPrimitivePaneRenderer {
  private _data: DisplacementRenderData[] = [];

  update(data: DisplacementRenderData[]): void {
    this._data = data;
  }

  /**
   * One chevron: an arrowhead pointing up (bullish) or down (bearish).
   * `cy` is the tip; the arms fall away behind it.
   */
  private _chevron(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    halfW: number,
    depth: number,
    up: boolean,
  ): void {
    const back = up ? depth : -depth;
    ctx.beginPath();
    ctx.moveTo(cx - halfW, cy + back);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx + halfW, cy + back);
    ctx.stroke();
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;

      for (const d of this._data) {
        const x = d.x * hr;
        const colour = d.skipped ? DIM : d.isBullish ? BULL : BEAR;
        const alpha = STATUS_ALPHA[d.status] ?? 1.0;

        // ---- Fair value gap band (drawn first, sits behind everything) ----
        if (d.fvg) {
          ctx.save();
          ctx.globalAlpha = alpha * 0.14;
          ctx.fillStyle = colour;
          ctx.fillRect(
            x,
            d.fvg.top * vr,
            d.fvg.right * hr - x,
            (d.fvg.bottom - d.fvg.top) * vr,
          );
          ctx.globalAlpha = alpha * 0.5;
          ctx.strokeStyle = colour;
          ctx.lineWidth = 1 * hr;
          ctx.setLineDash([3 * hr, 3 * hr]);
          ctx.beginPath();
          ctx.moveTo(x, d.fvg.top * vr);
          ctx.lineTo(d.fvg.right * hr, d.fvg.top * vr);
          ctx.moveTo(x, d.fvg.bottom * vr);
          ctx.lineTo(d.fvg.right * hr, d.fvg.bottom * vr);
          ctx.stroke();
          ctx.restore();
        }

        // ---- Body outline: marks exactly which candle displaced ----
        ctx.save();
        ctx.globalAlpha = alpha * 0.95;
        ctx.strokeStyle = colour;
        ctx.lineWidth = 1.5 * hr;
        if (d.skipped) ctx.setLineDash([3 * hr, 2 * hr]);
        const bx = x - d.halfWidth * hr;
        const bw = d.halfWidth * 2 * hr;
        const by = d.yBodyTop * vr;
        const bh = Math.max((d.yBodyBottom - d.yBodyTop) * vr, 1 * vr);
        ctx.strokeRect(bx, by, bw, bh);
        ctx.globalAlpha = alpha * 0.1;
        ctx.fillStyle = colour;
        ctx.fillRect(bx, by, bw, bh);
        ctx.restore();

        if (d.glyph === false) continue;

        // ---- Triple chevron with motion trail: the displacement glyph ----
        const up = d.isBullish;
        const yTip = d.yGlyph * vr;
        const halfW = 5 * hr;
        const depth = CHEVRON_DEPTH * vr;
        // Trailing chevrons sit BEHIND the tip (below for bullish, above for bearish)
        const step = CHEVRON_STEP * vr * (up ? 1 : -1);

        ctx.save();
        ctx.strokeStyle = colour;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let i = 0; i < CHEVRON_COUNT; i++) {
          // Lead chevron is boldest; the trail fades to convey thrust.
          ctx.globalAlpha = alpha * (1 - i * 0.3);
          ctx.lineWidth = (2.2 - i * 0.5) * hr;
          this._chevron(ctx, x, yTip + step * i, halfW, depth, up);
        }
        ctx.restore();

        // ---- Strike-through for rule-suppressed signals (ATH guard) ----
        if (d.skipped) {
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.strokeStyle = DIM;
          ctx.lineWidth = 1.5 * hr;
          ctx.lineCap = 'round';
          const r = 8;
          ctx.beginPath();
          ctx.moveTo(x - r * hr, yTip + (up ? r : -r) * vr * 0.6);
          ctx.lineTo(x + r * hr, yTip - (up ? r : -r) * vr * 0.6);
          ctx.stroke();
          ctx.restore();
        }

        // ---- Grade + range readout, stacked past the trail ----
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = colour;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const labelStep = (up ? LABEL_STEP : -LABEL_STEP) * vr;
        const labelY = yTip - labelStep;
        ctx.font = `bold ${GRADE_FONT * hr}px Arial`;
        ctx.fillText(d.grade, x, labelY);
        ctx.globalAlpha = alpha * 0.75;
        ctx.font = `${RANGE_FONT * hr}px Arial`;
        ctx.fillText(d.rangePoints.toFixed(1), x, labelY - labelStep);
        ctx.restore();
      }
      ctx.globalAlpha = 1.0;
      ctx.setLineDash([]);
    });
  }
}

class DisplacementPaneView implements IPrimitivePaneView {
  private _source: DisplacementPrimitive;
  private _renderer = new DisplacementPaneRenderer();

  constructor(source: DisplacementPrimitive) {
    this._source = source;
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData());
    return this._renderer;
  }
}

export class DisplacementPrimitive implements ISeriesPrimitive<Time> {
  private _levels: DisplacementLevel[] = [];
  private _showFvg = true;
  private _paneView: DisplacementPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _layout = new MarkerLayoutClient('displacement', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new DisplacementPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: the fixed lane alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /**
   * The chevron block (trail, tip, grade, range readout) as one box. The body
   * outline and the FVG band are not glyphs: they stay on the candle / price.
   */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    const h = DISPLACEMENT_BAND.outer - DISPLACEMENT_BAND.inner;
    for (const level of this._levels) {
      const t = Math.floor(level.timestamp / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) continue;
      const x = ctx.timeToX(t);
      const bull = level.direction === 'bullish';
      const y = ctx.priceToY(bull ? level.high : level.low);
      if (x === null || y === null) continue;
      const w = Math.max(
        12,
        ctx.measure(level.grade, `bold ${GRADE_FONT}px Arial`),
        ctx.measure(level.rangePoints.toFixed(1), `${RANGE_FONT}px Arial`),
        level.skipped ? 18 : 0,
      );
      out.push({
        key: level.id,
        time: t,
        x,
        side: bull ? 'above' : 'below',
        anchorY: y,
        w: Math.ceil(w) + 2,
        h: Math.ceil(h),
        gap: 3,
        priority: LAYOUT_PRIORITY.displacement,
        color: level.skipped ? DIM : bull ? BULL : BEAR,
        label: `displacement ${level.grade} ${level.rangePoints.toFixed(1)}`,
        ref: level,
      });
    }
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Positions are recomputed from source data on every render pass.
  }

  setLevels(levels: DisplacementLevel[]): void {
    this._levels = levels;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setShowFvg(show: boolean): void {
    this._showFvg = show;
    this._requestUpdate?.();
  }

  getRendererData(): DisplacementRenderData[] {
    if (!this._series || !this._chart) return [];

    const timeScale = this._chart.timeScale();
    const barSpacing = (timeScale.options() as any)?.barSpacing ?? 6;
    const halfWidth = Math.max(2, barSpacing * 0.35);
    const out: DisplacementRenderData[] = [];
    // Shared layout: where each chevron block landed (tip GLYPH_OFFSET - inner past its near edge).
    const shared = this._layout.placed();
    const tipY = new Map<DisplacementLevel, number>();
    if (shared) {
      const tipFromNear = GLYPH_OFFSET - DISPLACEMENT_BAND.inner;
      for (const p of shared) {
        const level = p.item.ref as DisplacementLevel;
        tipY.set(level, level.direction === 'bullish' ? p.top + p.h - tipFromNear : p.top + tipFromNear);
      }
    }

    for (const level of this._levels) {
      const x = timeScale.timeToCoordinate(Math.floor(level.timestamp / 1000) as Time);
      if (x === null) continue;

      const yHigh = this._series.priceToCoordinate(level.high);
      const yLow = this._series.priceToCoordinate(level.low);
      const yOpen = this._series.priceToCoordinate(level.open);
      const yClose = this._series.priceToCoordinate(level.close);
      if (yHigh === null || yLow === null || yOpen === null || yClose === null) continue;

      const isBullish = level.direction === 'bullish';
      // Bullish glyph rides above the high, bearish below the low.
      // In the shared layout it rides where its box landed; a folded one is a "+N" chip.
      const glyph = !shared || tipY.has(level);
      const yGlyph = tipY.get(level) ?? (isBullish ? yHigh - GLYPH_OFFSET : yLow + GLYPH_OFFSET);

      let fvg: DisplacementRenderData['fvg'] = null;
      if (this._showFvg && level.createdFvg && level.fvgTop !== null && level.fvgBottom !== null) {
        const top = this._series.priceToCoordinate(level.fvgTop);
        const bottom = this._series.priceToCoordinate(level.fvgBottom);
        if (top !== null && bottom !== null) {
          fvg = {
            top: Math.min(top, bottom),
            bottom: Math.max(top, bottom),
            right: x + barSpacing * FVG_EXTEND_BARS,
          };
        }
      }

      out.push({
        x,
        yGlyph,
        yBodyTop: Math.min(yOpen, yClose),
        yBodyBottom: Math.max(yOpen, yClose),
        halfWidth,
        isBullish,
        grade: level.grade,
        rangePoints: level.rangePoints,
        status: level.status,
        skipped: level.skipped,
        fvg,
        glyph,
      });
    }

    return out;
  }
}
